import {
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  ArrowUpDown,
  AudioLines,
  Bell,
  BellOff,
  Bot,
  ShieldOff,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp,
  Circle,
  CircleDot,
  Crown,
  Download,
  Ellipsis,
  Eye,
  EyeOff,
  File as ArquivoIcone,
  Filter,
  Folder,
  Gauge,
  Globe,
  Group,
  History,
  Hourglass,
  LayoutPanelLeft,
  Lock,
  LockOpen,
  Menu,
  LayoutGrid,
  Mic,
  Monitor,
  Moon,
  Paperclip,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Send,
  Settings,
  Slash,
  SlidersHorizontal,
  Star,
  Sun,
  SunMoon,
  Trash2,
  TriangleAlert,
  Users,
  Wrench,
  X,
} from 'lucide-react';

/**
 * Os ícones do painel, num lugar só.
 *
 * Antes cada tela escolhia um caractere Unicode à mão — `◧` para o trabalho,
 * `▥` para máquinas, `☺` para contas, um emoji de microfone no compositor. Três
 * problemas nisso: o desenho muda conforme a fonte instalada na máquina de quem
 * olha, o alinhamento vertical nunca bate com o texto ao lado, e emoji vem
 * colorido por decisão do sistema operacional — no meio de uma barra cinza,
 * parece adesivo.
 *
 * Reexportar daqui, em vez de importar `lucide-react` em cada arquivo, mantém
 * uma única lista do que o painel usa: trocar de biblioteca um dia é mexer
 * neste arquivo, e a próxima pessoa vê num relance o vocabulário visual
 * inteiro.
 */

/** O tamanho padrão: 16 px casa com o texto de 14 px das barras. */
export const TAMANHO_ICONE = 16;

export type PropsDeIcone = {
  /** Em px. O padrão serve para quase tudo; 14 para dentro de linha de lista. */
  tamanho?: number;
  className?: string;
};

function envolver(Componente: typeof Mic) {
  return function Icone({ tamanho = TAMANHO_ICONE, className }: PropsDeIcone): React.JSX.Element {
    /**
     * `aria-hidden` sempre: todo ícone daqui vive dentro de um botão ou link
     * que já tem nome acessível (`aria-label`, `title` ou texto ao lado).
     * Anunciá-lo de novo faria o leitor de tela ler duas vezes a mesma coisa.
     */
    return (
      <Componente
        size={tamanho}
        strokeWidth={1.75}
        aria-hidden="true"
        focusable="false"
        className={'shrink-0 ' + (className ?? '')}
      />
    );
  };
}

// Menu lateral
export const IconeTrabalho = envolver(LayoutPanelLeft);
export const IconeAudio = envolver(AudioLines);
export const IconeMaquinas = envolver(Monitor);
export const IconeContas = envolver(Users);
export const IconeSaude = envolver(Settings);
export const IconeAvisos = envolver(Bell);
/** Sino cortado: esta conversa não avisa mais. Fica na própria linha da lista. */
export const IconeSilenciada = envolver(BellOff);
export const IconeUso = envolver(ChartColumn);
/**
 * A tela de Configurações (F17). Não é a engrenagem: ela já é da Saúde, e dois
 * itens do menu com o mesmo desenho obrigariam a ler o nome para saber qual é.
 */
export const IconeConfiguracoes = envolver(SlidersHorizontal);
/** A tela admin de atividade: o que cada pessoa fez, em ordem de tempo. */
export const IconeAtividade = envolver(History);
/** O Toolkit (F3.7): as rotinas validadas e o dicionário de memória. */
export const IconeToolkit = envolver(Wrench);
export const IconeMenu = envolver(Menu);

// Tema
export const IconeTemaSistema = envolver(SunMoon);
export const IconeTemaClaro = envolver(Sun);
export const IconeTemaEscuro = envolver(Moon);

// Compositor e conversa
export const IconeMicrofone = envolver(Mic);
/** A saida da Prosa para o painel de trabalho — a grade da referencia. */
export const IconeGrade = envolver(LayoutGrid);
export const IconeEnviar = envolver(Send);
export const IconeAnexar = envolver(Paperclip);
export const IconeComandos = envolver(Slash);
export const IconeMais = envolver(Plus);
export const IconeFechar = envolver(X);
export const IconeConfirmado = envolver(Check);
export const IconeAbrir = envolver(ChevronDown);
export const IconeFechado = envolver(ChevronRight);
export const IconeDescer = envolver(ArrowDown);
export const IconeSubir = envolver(ArrowUp);
export const IconeEsperando = envolver(Hourglass);
export const IconeMaisOpcoes = envolver(Ellipsis);
export const IconeAviso = envolver(TriangleAlert);
export const IconeEditar = envolver(Pencil);
export const IconeMestre = envolver(Crown);
export const IconeCota = envolver(Gauge);
/** Sessao que age sem cartao de permissao (modo auto ou bypassPermissions). */
export const IconeSemPortao = envolver(ShieldOff);

// Estado e explorador
export const IconePreso = envolver(Lock);
export const IconeSolto = envolver(LockOpen);
export const IconePonto = envolver(CircleDot);
export const IconeCirculo = envolver(Circle);
export const IconePasta = envolver(Folder);
export const IconeArquivo = envolver(ArquivoIcone);
/** Baixar o anexo que a sessão mandou para a conversa. */
export const IconeBaixar = envolver(Download);

/** A barra que arruma a lista de sessões: agrupar, ordenar, filtrar. */
export const IconeAgrupar = envolver(Group);
export const IconeOrdenar = envolver(ArrowUpDown);
export const IconeFiltro = envolver(Filter);
/** Limpar o arranjo da lista: devolve o agrupamento e a ordem de sempre. */
export const IconeLixeira = envolver(Trash2);

/** Paginação de pergunta e de pedido, e o recolher que devolve a conversa. */
export const IconeAnterior = envolver(ChevronLeft);
export const IconeProximo = envolver(ChevronRight);
export const IconeMinimizar = envolver(ChevronsDownUp);
/** Subagentes: a tela onde o trabalho deles acontece. */
export const IconeAgente = envolver(Bot);
/** Recomecar a mestre: conversa nova, mesma memoria. */
export const IconeRecomecar = envolver(RotateCcw);

/** Mostrar e esconder o que foi digitado num campo de segredo. */
export const IconeMostrar = envolver(Eye);
export const IconeOcultar = envolver(EyeOff);
/** O campo de busca dentro de um seletor de lista grande. */
export const IconeBuscar = envolver(Search);
/** Um modelo marcado como favorito no catálogo do OpenCode. */
export const IconeFavorito = envolver(Star);
/** O conteúdo sai da rede de casa (o provedor de modelos da frota e o provedor do modelo). */
export const IconeForaDeCasa = envolver(Globe);
/** A conversa do OpenCode passou para outro modelo porque o provedor dela saiu (F2, D46). */
export const IconeTrocaDeModelo = envolver(ArrowRightLeft);
